import React, { FC, useState } from "react"
import { Alert, Button, Card, Form, Input, Result } from "antd"
import { Formik, FormikProps } from "formik"
import * as Yup from "yup"
import { useTranslation } from "react-i18next"
import { TFunction } from "i18next"
import { useHistory, useLocation } from "react-router-dom"
import css from "./Widerruf.module.css"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import { ApolloError } from "@apollo/client"
import { useCreateWithdrawalEndUserAppMutation, useGetCompanyQuery } from "../../generated/graphql"
import { URL_BASE } from "../../models/url"

// Formularwerte. Das Honeypot-Feld muss leer bleiben (Bot-Falle).
type WithdrawalFormValues = {
    name: string
    bookingReference: string
    email: string
    reason: string
    // Unauffälliger Honeypot-Feldname. Leer = Mensch.
    company: string
}

const buildInitialValues = (bookingReference: string): WithdrawalFormValues => ({
    name: "",
    bookingReference,
    email: "",
    reason: "",
    company: "",
})

const buildValidationSchema = (t: TFunction): Yup.AnyObjectSchema =>
    Yup.object().shape({
        name: Yup.string().required(t("withdrawal.form.name.required", "Bitte gib deinen Namen an.")),
        bookingReference: Yup.string()
            .required(t("withdrawal.form.bookingReference.required", "Bitte gib deine Buchungsnummer an."))
            .max(
                255,
                t(
                    "withdrawal.form.bookingReference.tooLong",
                    "Die Buchungsnummer ist zu lang (maximal 255 Zeichen)."
                )
            ),
        email: Yup.string()
            .email(t("withdrawal.form.email.invalid", "Bitte gib eine gültige E-Mail-Adresse ein."))
            .required(t("withdrawal.form.email.required", "Bitte gib deine E-Mail-Adresse an.")),
        reason: Yup.string().max(
            5000,
            t("withdrawal.form.reason.tooLong", "Der Grund darf höchstens 5000 Zeichen lang sein.")
        ),
        // Honeypot ist optional und darf leer sein.
        company: Yup.string(),
    })

const Widerruf: FC = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    // Buchungsnummer aus dem Link (?bookingNumber=…) vorbefüllen (z. B. aus „Meine Buchungen").
    const initialValues = buildInitialValues(new URLSearchParams(location.search).get("bookingNumber") ?? "")
    const { data: companyData } = useGetCompanyQuery()
    const providerName =
        companyData?.companyPublic.name ?? t("withdrawal.providerFallback", "der Kursanbieter")

    const [submitted, setSubmitted] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [createWithdrawal, { loading }] = useCreateWithdrawalEndUserAppMutation()

    const validationSchema = buildValidationSchema(t)

    const genericSubmitError = t(
        "withdrawal.form.submitError",
        "Der Widerruf konnte nicht übermittelt werden. Bitte versuche es erneut oder wende dich an den Anbieter."
    )

    const handleSubmit = async (values: WithdrawalFormValues) => {
        setSubmitError(null)

        // Honeypot gefüllt -> still als Erfolg behandeln, nichts senden (Bot).
        if (values.company.trim() !== "") {
            setSubmitted(true)
            return
        }

        try {
            const { data } = await createWithdrawal({
                variables: {
                    input: {
                        name: values.name,
                        bookingReference: values.bookingReference,
                        email: values.email,
                        reason: values.reason.trim() === "" ? undefined : values.reason,
                        honeypot: values.company,
                    },
                },
            })

            if (data?.createWithdrawalEndUserApp.success) {
                setSubmitted(true)
            } else {
                // Server hat ohne Exception, aber ohne Erfolg geantwortet.
                setSubmitError(genericSubmitError)
            }
        } catch (error) {
            // Netzwerk-/GraphQL-Fehler: dem Nutzer sichtbares Feedback geben, damit
            // er nicht fälschlich annimmt, der Widerruf sei bereits eingegangen.
            // Verständliche Backend-Meldung (z. B. Rate-Limit „Zu viele Anfragen …")
            // bevorzugt anzeigen, sonst generische Fallback-Meldung.
            const backendMessage =
                error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined
            setSubmitError(
                backendMessage && backendMessage.trim() !== "" ? backendMessage : genericSubmitError
            )
        }
    }

    if (submitted) {
        return (
            <div className={css.container}>
                <Card className={css.card}>
                    <Result
                        status='success'
                        title={t("withdrawal.success.title", "Eingang bestätigt")}
                        subTitle={t(
                            "withdrawal.success.subTitle",
                            "Wir haben deine Widerrufserklärung erhalten. Du erhältst in Kürze eine Eingangsbestätigung per E-Mail."
                        )}
                        extra={
                            <Button type='primary' onClick={() => history.push(URL_BASE)}>
                                {t("withdrawal.success.backToSearch", "Zurück zur Kurssuche")}
                            </Button>
                        }
                    />
                </Card>
            </div>
        )
    }

    return (
        <div className={css.container}>
            <Card className={css.card}>
                <div className={css.content}>
                    <h1>{t("withdrawal.heading", "Widerruf deiner Buchung / deines Vertrags")}</h1>
                    <p>
                        {t(
                            "withdrawal.intro",
                            "Hier kannst du eine Widerrufserklärung zu einem online geschlossenen Vertrag abgeben."
                        )}
                    </p>

                    <h2>{t("withdrawal.howItWorks.title", "So funktioniert es:")}</h2>
                    <ol>
                        <li>
                            {t(
                                "withdrawal.howItWorks.step1",
                                "Fülle die drei Felder aus (Name, Buchungsnummer, E-Mail-Adresse). Einen Grund musst du nicht angeben."
                            )}
                        </li>
                        <li>
                            {t(
                                "withdrawal.howItWorks.step2",
                                'Mit Klick auf „Widerruf bestätigen" gibst du deine Widerrufserklärung verbindlich ab.'
                            )}
                        </li>
                        <li>
                            {t(
                                "withdrawal.howItWorks.step3",
                                "Du erhältst unverzüglich eine Eingangsbestätigung per E-Mail mit dem Inhalt deiner Erklärung sowie Datum und Uhrzeit des Eingangs."
                            )}
                        </li>
                    </ol>

                    <p className={css.legalNote}>
                        {t("withdrawal.legalNote", {
                            providerName,
                            defaultValue:
                                "Über die weitere Abwicklung (z. B. eine etwaige Rückerstattung bereits gezahlter Beträge) informiert dich {{providerName}} gesondert. Ob für deine konkrete Buchung ein gesetzliches Widerrufsrecht besteht, richtet sich nach den vertraglichen und gesetzlichen Bestimmungen; bei termingebundenen Kursen kann ein gesetzliches Widerrufsrecht ausgeschlossen sein.",
                        })}
                    </p>
                </div>

                {submitError && (
                    <Alert
                        type='error'
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={submitError}
                    />
                )}

                <Formik<WithdrawalFormValues>
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {(formikProps: FormikProps<WithdrawalFormValues>) => {
                        const { values, handleSubmit: formikSubmit, isValid, dirty } = formikProps
                        const { handleTextInputChange, handleBlur, validateStatus, help } =
                            createInputHelpers(formikProps)

                        return (
                            <Form layout='vertical' className={css.form} onFinish={() => formikSubmit()}>
                                <Form.Item
                                    label={t("withdrawal.form.name.label", "Name")}
                                    required
                                    validateStatus={validateStatus("name")}
                                    help={help("name")}
                                >
                                    <Input
                                        value={values.name}
                                        onChange={handleTextInputChange("name")}
                                        onBlur={handleBlur("name")}
                                        autoComplete='name'
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={t(
                                        "withdrawal.form.bookingReference.label",
                                        "Buchungsnummer"
                                    )}
                                    required
                                    validateStatus={validateStatus("bookingReference")}
                                    help={help("bookingReference")}
                                >
                                    <Input
                                        value={values.bookingReference}
                                        onChange={handleTextInputChange("bookingReference")}
                                        onBlur={handleBlur("bookingReference")}
                                        autoComplete='off'
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={t("withdrawal.form.email.label", "E-Mail-Adresse")}
                                    required
                                    validateStatus={validateStatus("email")}
                                    help={help("email")}
                                >
                                    <Input
                                        type='email'
                                        value={values.email}
                                        onChange={handleTextInputChange("email")}
                                        onBlur={handleBlur("email")}
                                        autoComplete='email'
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={t("withdrawal.form.reason.label", "Grund (optional)")}
                                    validateStatus={validateStatus("reason")}
                                    help={help("reason")}
                                >
                                    <Input.TextArea
                                        rows={3}
                                        value={values.reason}
                                        onChange={handleTextInputChange("reason")}
                                        onBlur={handleBlur("reason")}
                                    />
                                </Form.Item>

                                {/* Honeypot-Feld: visuell ausgeblendet (off-screen), für simple Bots
                                    nicht als display:none erkennbar. aria-hidden + tabindex=-1 +
                                    autocomplete=off halten es von echten Nutzern fern. */}
                                <div className={css.honeypot} aria-hidden='true'>
                                    <label htmlFor='company-field'>
                                        {t("withdrawal.form.honeypotLabel", "Bitte dieses Feld leer lassen")}
                                    </label>
                                    <input
                                        id='company-field'
                                        type='text'
                                        name='company'
                                        value={values.company}
                                        onChange={handleTextInputChange("company")}
                                        tabIndex={-1}
                                        autoComplete='off'
                                    />
                                </div>

                                <Button
                                    type='primary'
                                    htmlType='submit'
                                    loading={loading}
                                    disabled={!(isValid && dirty)}
                                    className={css.submitButton}
                                >
                                    {t("withdrawal.form.submit", "Widerruf bestätigen")}
                                </Button>
                            </Form>
                        )
                    }}
                </Formik>
            </Card>
        </div>
    )
}

export default Widerruf
